import { supabase } from "./supabase";
import {
	getClientTimeoutForPromptType,
	getClientTimeoutForOperation,
	getCheckInAnalysisMaxCheckins,
	getCheckInAnalysisMaxMonths,
	ALL_PROMPTS,
} from "./prompt-config";

export interface AIPromptType {
	overall_context: string;
	reflection_qa: string;
	reflection_summary: string;
	contributing_factors: string;
	signals: string;
	corrective_actions: string;
	future_state: string;
	suggested_reflection: string;
}

// Strongly-typed AI response shapes
export interface TitleDescription {
	title: string;
	description: string;
}
export type AITextEnvelope =
	| { response: string }
	| { text: string }
	| { content: string }
	| { answer: string };
export type AIActionsEnvelope<T> =
	| { actions: T[] }
	| { corrective_actions: T[] };
export type AISummaryEnvelope = { summary: TitleDescription };
export type SummaryResult =
	| TitleDescription
	| AISummaryEnvelope
	| AITextEnvelope
	| string;
export type ListItem = TitleDescription;
export type ListResult =
	| ListItem[]
	| AIActionsEnvelope<ListItem>
	| AITextEnvelope
	| string;
export type FutureStateResult =
	| { description: string }
	| AITextEnvelope
	| string;

export class AIService {
	public prompts: Partial<AIPromptType> = {};

	constructor() {
		// Load prompts statically from configuration
		try {
			this.prompts = ALL_PROMPTS;
			console.log(
				"✅ Loaded prompts from config/ai-prompts.json:",
				Object.keys(this.prompts),
			);
		} catch (error) {
			console.error("❌ Error loading AI prompts from configuration:", error);
			throw new Error(
				`Failed to load AI prompts from config/ai-prompts.json: ${error instanceof Error ? error.message : "Unknown error"}. ` +
					`Please ensure the configuration file exists and is valid.`,
			);
		}
	}

	private logEdgeFunctionError(
		invokeError: any,
		attempt: number,
	): { message: string; status: number } {
		const status = invokeError.status || 500;
		let debugMessage = `Function invoke failed (attempt ${attempt}): ${invokeError.message || invokeError}`;

		// Handle specific error types
		if (invokeError.message?.includes("Failed to send a request")) {
			debugMessage +=
				" - Network connectivity issue. Check Supabase URL and internet connection.";
			if (attempt === 1) {
				console.warn("💡 Check VITE_SUPABASE_URL environment variable");
				console.warn("💡 Verify Supabase project is accessible");
			}
		} else if (status === 404) {
			debugMessage +=
				" - Edge Function not found. Functions may not be deployed to this Supabase project.";
			if (attempt === 1) {
				console.warn(
					"💡 To deploy Edge Functions: supabase functions deploy --project-ref [ref]]",
				);
			}
		} else if (status === 500) {
			debugMessage +=
				" - Server error. Check function code or environment variables (e.g., OPENAI_API_KEY).";
			if (attempt === 1) {
				console.warn(
					"💡 Set OpenAI key: supabase secrets set OPENAI_API_KEY=your_key --project-ref [ref]]",
				);
			}
		} else if (status === 401 || status === 403) {
			debugMessage +=
				" - Authentication error. Check API keys and permissions.";
		} else if (
			invokeError.name === "TypeError" ||
			invokeError.name === "NetworkError"
		) {
			debugMessage +=
				" - Network or connection error. Check Supabase configuration.";
		}

		console.error(debugMessage);
		return {
			message: invokeError.message || "Edge Function call failed",
			status: status,
		};
	}

	private getTimeoutForPromptType(promptType: keyof AIPromptType): number {
		try {
			return getClientTimeoutForPromptType(promptType);
		} catch {
			return 60000;
		}
	}

	private getTimeoutForOperation(
		operation: "checkin_analysis" | "conversation",
	): number {
		try {
			return getClientTimeoutForOperation(operation as any);
		} catch {
			return 60000;
		}
	}

	async callOpenAI(
		promptType: keyof AIPromptType,
		userInput: string,
	): Promise<any> {
		// 1. Format local prompts (Legacy support)
		// Note: modifying class state like this can have side effects.
		if (this.prompts[promptType]) {
			this.prompts[promptType] = this.prompts[promptType].replace(
				/\{\{ACTION_COUNT\}\}/g,
				"6",
			);
		}

		// 2. Local Auth Check (Fail fast without network overhead)
		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session?.user || !session?.access_token) {
			throw new Error(`AI service requires authentication. Please sign in.`);
		}

		const MAX_RETRIES = 3;
		let lastError: any;

		for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
			try {
				const timeoutMs = this.getTimeoutForPromptType(promptType);

				// 3. Invoke with Timeout
				const invokePromise = supabase.functions.invoke("ai-generate", {
					body: { promptType, userInput },
				});

				const timeoutPromise = new Promise((_, reject) =>
					setTimeout(
						() => reject(new Error(`Request timed out after ${timeoutMs}ms`)),
						timeoutMs,
					),
				);

				// Race the request against the timer
				const { data, error } = (await Promise.race([
					invokePromise,
					timeoutPromise,
				])) as any;

				// 4. Handle Supabase/Network Errors
				if (error) {
					throw new Error(error.message || "Unknown Edge Function error");
				}

				// 5. Handle Application Errors (Returned inside data)
				if (!data) throw new Error("No data received from AI service");
				if (data.error) throw new Error(data.error);

				// Success
				return data;
			} catch (err: any) {
				lastError = err;
				console.warn(
					`AI Service Attempt ${attempt}/${MAX_RETRIES} failed: ${err.message}`,
				);

				// Don't wait after the last attempt
				if (attempt < MAX_RETRIES) {
					// Exponential backoff: 1s, 2s, 3s...
					await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
				}
			}
		}

		// 6. Final Failure
		console.error("AI Service Fatal Error:", lastError);
		throw new Error(
			`AI service failed for ${promptType}. Please check your connection or try again. (${lastError.message})`,
		);
	}
	async askReflectionQuestion(
		allQuestionsAndAnswers: Array<{ question: string; answer: string }>,
		themePrompt?: string,
		previousResponseId?: string,
	): Promise<{
		question: string;
		responseId?: string;
		model?: string;
		reasoning?: string;
		summary?: { title: string; description: string };
	}> {
		const MAX_ATTEMPTS = 3;

		for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
			try {
				// 1. Setup Timeout
				const timeoutMs = this.getTimeoutForOperation("conversation");
				const timeoutPromise = new Promise((_, reject) => {
					setTimeout(
						() =>
							reject(new Error(`Edge Function timeout after ${timeoutMs}ms`)),
						timeoutMs,
					);
				});

				// 2. Validate Session
				const {
					data: { session },
				} = await supabase.auth.getSession();
				if (!session?.access_token) {
					throw new Error("Session expired. Please sign in again.");
				}

				// 3. Prepare Payload
				const body: Record<string, any> = {
					qaHistory: allQuestionsAndAnswers || [],
				};
				if (themePrompt) body.themePrompt = themePrompt;
				if (previousResponseId) {
					body.previousResponseId = previousResponseId;
					console.log("🔗 Using previous response ID:", previousResponseId);
				}

				// 4. Invoke Edge Function with Timeout Race
				const invokePromise = supabase.functions.invoke("ai-conversation", {
					body,
				});

				// Race the invocation against the timeout
				const result: any = await Promise.race([invokePromise, timeoutPromise]);
				const { data, error } = result;

				// 5. Handle Edge Function Errors
				if (error) {
					console.error(`ai-conversation error (attempt ${attempt}):`, error);

					// If this is the last attempt, or if it's a fatal configuration error, stop.
					if (attempt === MAX_ATTEMPTS) {
						throw new Error(
							`AI Service failed: ${error.message || "Unknown Edge Function error"}`,
						);
					}

					// Backoff before retry
					await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
					continue;
				}

				// 6. Validate Response Data
				if (!data || !data.question) {
					throw new Error("Invalid response format received from AI service.");
				}

				// Log useful context for debugging
				if (data.summary)
					console.log("📝 Received decision summary:", data.summary);
				if (data.responseId)
					console.log("💾 Received response ID:", data.responseId);

				return {
					question: data.question,
					responseId: data.responseId,
					model: data.model,
					reasoning: data.reasoning,
					summary: data.summary,
				};
			} catch (err: any) {
				console.warn(`Attempt ${attempt} failed:`, err.message || err);

				// If we exhausted retries, throw the final error
				if (attempt === MAX_ATTEMPTS) {
					throw new Error(
						`AI conversation service unavailable: ${err.message || "Request failed"}`,
					);
				}

				// Wait before retry
				await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
			}
		}

		throw new Error("AI conversation service failed unexpectedly.");
	}
	async generateContributingFactors(reflectionData: {
		title: string;
		description: string;
		qaResults: any;
		existingFactors?: Array<{ title: string; description: string }>;
	}): Promise<ListResult> {
		let input = `Reflection Title: ${reflectionData.title}\nDescription: ${reflectionData.description}\nQ&A Results: ${JSON.stringify(reflectionData.qaResults)}`;

		if (
			reflectionData.existingFactors &&
			reflectionData.existingFactors.length > 0
		) {
			input += `\n\nExisting Contributing Factors (generate 3 NEW and DISTINCT factors that are different from these):\n${reflectionData.existingFactors.map((f) => `- ${f.title}: ${f.description}`).join("\n")}`;
		}

		input += `\n\nCRITICAL: Return your response as a valid JSON array in exactly this format:\n[\n  {\n    "title": "Factor Title Here",\n    "description": "Detailed description of the factor here"\n  },\n  {\n    "title": "Second Factor Title",\n    "description": "Detailed description of the second factor"\n  }\n]\n\nDo not include any text before or after the JSON array. The response must be valid JSON that can be parsed directly.`;

		return this.callOpenAI("contributing_factors", input);
	}

	async generateSignals(reflectionData: {
		title: string;
		description: string;
		qaResults: any;
		existingSignals?: Array<{ title: string; description: string }>;
	}): Promise<ListResult> {
		let input = `Reflection Title: ${reflectionData.title}\nDescription: ${reflectionData.description}\nQ&A Results: ${JSON.stringify(reflectionData.qaResults)}`;

		if (
			reflectionData.existingSignals &&
			reflectionData.existingSignals.length > 0
		) {
			input += `\n\nExisting Signals (generate 3 NEW and DISTINCT signals that are different from these):\n${reflectionData.existingSignals.map((s) => `- ${s.title}: ${s.description}`).join("\n")}`;
		}

		input += `\n\nCRITICAL: Return your response as a valid JSON array in exactly this format:\n[\n  {\n    "title": "Signal Title Here",\n    "description": "Detailed description of the signal here"\n  },\n  {\n    "title": "Second Signal Title",\n    "description": "Detailed description of the second signal"\n  }\n]\n\nDo not include any text before or after the JSON array. The response must be valid JSON that can be parsed directly.`;

		return this.callOpenAI("signals", input);
	}

	async generateCorrectiveActions(reflectionData: {
		title: string;
		description: string;
		futureState: string;
		contributingFactors: any[];
		signals: any[];
		existingActions?: Array<{ title: string; description: string }>;
	}): Promise<ListResult> {
		let input = `Current State: ${reflectionData.title}
Description: ${reflectionData.description}

Future State Vision: ${reflectionData.futureState}

Contributing Factors:
${reflectionData.contributingFactors.map((f) => `- ${f.title}: ${f.description}`).join("\n")}

Signals:
${reflectionData.signals.map((s) => `- ${s.title}: ${s.description}`).join("\n")}

Create corrective actions that help transition from the current state to the future state by addressing these contributing factors and signals.

CRITICAL: Return your response as a valid JSON array in exactly this format:
[
  {
    "title": "Action Title Here",
    "description": "Brief, concise description (2-3 sentences maximum) of the action here"
  },
  {
    "title": "Second Action Title",
    "description": "Brief, concise description (2-3 sentences maximum) of the second action"
  }
]

IMPORTANT: Keep descriptions concise and direct - maximum 2-3 sentences. Focus on the essential action and why it helps, without lengthy explanations.

Do not include any text before or after the JSON array. The response must be valid JSON that can be parsed directly.`;

		if (
			reflectionData.existingActions &&
			reflectionData.existingActions.length > 0
		) {
			input += `\n\nExisting Corrective Actions (generate 3 NEW and DISTINCT actions that are different from these):\n${reflectionData.existingActions.map((a) => `- ${a.title}: ${a.description}`).join("\n")}`;
		}

		return this.callOpenAI("corrective_actions", input);
	}

	async generateFutureState(
		reflectionDescription: string,
	): Promise<FutureStateResult> {
		return this.callOpenAI("future_state", reflectionDescription);
	}

	async generateReflectionSummary(
		qaHistory: Array<{ question: string; answer: string }>,
	): Promise<SummaryResult> {
		console.log(
			"🔍 GenerateReflectionSummary called with",
			qaHistory.length,
			"Q&A pairs",
		);

		try {
			const conversationText = qaHistory
				.map(
					(qa, index) =>
						`Q${index + 1}: ${qa.question}\nA${index + 1}: ${qa.answer}`,
				)
				.join("\n\n");

			console.log(
				"🔍 Calling ai-generate edge function for reflection summary...",
			);
			const result = await this.callOpenAI(
				"reflection_summary",
				conversationText,
			);

			console.log("🔍 ai-generate result:", result);

			if (result && (result.summary || result.title)) {
				console.log("✅ Got valid summary from AI");
				return result.summary || result;
			} else {
				console.warn("����️ Invalid AI response format, using fallback");
				throw new Error("Invalid response format from AI");
			}
		} catch (error) {
			console.error("❌ Error generating AI summary:", error);
			throw new Error(
				`Unable to generate reflection summary: ${error instanceof Error ? error.message : "AI service not available"}`,
			);
		}
	}

	async analyzeCheckIns(
		checkIns: Array<{
			id: string;
			general_feeling: number;
			day_description: string;
			created_at: string;
		}>,
	): Promise<{
		mainTopics: Array<{ topic: string; frequency: number; sentiment: number }>;
		negativeTopics: Array<{
			topic: string;
			frequency: number;
			sentiment: number;
			impact: string;
			patterns: string;
		}>;
		positiveTopics: Array<{
			topic: string;
			frequency: number;
			sentiment: number;
			impact: string;
			patterns: string;
		}>;
		reflectionSuggestion: {
			title: string;
			description: string;
			reasoning: string;
		};
	}> {
		const timeoutMs = getClientTimeoutForOperation("checkin_analysis");
		const timeoutPromise = new Promise((_, reject) => {
			setTimeout(
				() =>
					reject(
						new Error(
							"AI analysis service is taking longer than expected. Please try again shortly.",
						),
					),
				timeoutMs,
			);
		});

		// Apply limits from configuration: within last M months, up to N check-ins
		const maxCheckins = getCheckInAnalysisMaxCheckins();
		const maxMonths = getCheckInAnalysisMaxMonths();
		const now = new Date();
		const cutoff = new Date(now);
		cutoff.setMonth(cutoff.getMonth() - maxMonths);
		const limitedCheckIns = [...checkIns]
			.filter((ci) => new Date(ci.created_at).getTime() >= cutoff.getTime())
			.sort(
				(a, b) =>
					new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
			)
			.slice(0, Math.max(0, maxCheckins));

		const {
			data: { session },
		} = await supabase.auth.getSession();
		if (!session?.access_token) {
			throw new Error("No active session found. Please sign in again.");
		}

		const result = (await Promise.race([
			supabase.functions.invoke("ai-checkin-analysis", {
				body: { checkIns: limitedCheckIns },
			}),
			timeoutPromise,
		])) as any;

		const { data, error } = result || {};

		if (error) {
			throw new Error(error.message || "AI analysis service failed.");
		}

		if (!data?.analysis) {
			throw new Error("AI analysis service returned invalid data.");
		}

		return data.analysis;
	}

	async analyzeReflectionProgress(
		reflectionData: {
			id: string;
			title: string;
			description: string;
		},
		checkIns: Array<{
			id: string;
			created_at: string;
			reflection_feeling: number | null;
			reflection_impact: string | null;
			reflection_reasons: string | null;
			signals_noticed: string[];
		}>,
	): Promise<{
		positiveObservations: Array<{
			observation: string;
			frequency: number;
			impact: string;
			signals: string[];
			context: string;
		}>;
		negativeObservations: Array<{
			observation: string;
			frequency: number;
			impact: string;
			signals: string[];
			context: string;
		}>;
		impactTrends: {
			overall: "improving" | "declining" | "stable";
			recentTrend: string;
			significantChanges: string[];
		};
		signalPatterns: Array<{
			signal: string;
			frequency: number;
			correlation: string;
			impact: string;
		}>;
		insights: {
			keyFindings: string[];
			progressIndicators: string[];
			concernAreas: string[];
		};
		recommendations: Array<{
			action: string;
			priority: "high" | "medium" | "low";
			reasoning: string;
			timeframe: string;
		}>;
	}> {
		const useEdgeFunction = true;

		if (!useEdgeFunction) {
			console.error(
				"❌ ai-reflection-analysis: AI service is disabled. Edge function needs to be deployed with OPENAI_API_KEY environment variable.",
			);
			throw new Error(
				"AI edge function for reflection analysis is not deployed or configured. Please deploy the edge function with OPENAI_API_KEY environment variable to enable analysis.",
			);
		}

		try {
			console.log("🤖 Calling reflection analysis edge function");
			console.log("📊 Reflection data:", {
				id: reflectionData.id,
				title: reflectionData.title,
				description: reflectionData.description?.substring(0, 100) + "...",
			});
			console.log(`📋 Check-ins data: ${checkIns.length} check-ins`);

			// Single attempt with generous timeout
			const timeoutMs = getClientTimeoutForOperation("reflection_analysis");
			const timeoutPromise = new Promise((_, reject) => {
				setTimeout(
					() => reject(new Error("Reflection analysis timed out")),
					timeoutMs,
				);
			});

			// Ensure Authorization header is present for the Edge Function
			const {
				data: { session },
			} = await supabase.auth.getSession();
			if (!session?.access_token) {
				throw new Error("No active session found. Please sign in again.");
			}

			const requestPromise = supabase.functions.invoke(
				"ai-reflection-analysis",
				{
					body: {
						reflection: reflectionData,
						checkIns: checkIns,
					},
					headers: {
						Authorization: `Bearer ${session.access_token}`,
					},
				},
			);

			const response = (await Promise.race([
				requestPromise,
				timeoutPromise,
			])) as any;
			const { data, error } = response || {};

			if (error) {
				console.error("ai-reflection-analysis: Edge function error:", error);
				console.error("ai-reflection-analysis: Full error context:", {
					error,
					data,
					errorMessage: error?.message,
					errorContext: error?.context,
					responseData: data,
				});
				if (data && data.error && (data.details || data.message)) {
					const errorMsg = `${data.error}: ${data.details || data.message}`;
					console.error(
						"ai-reflection-analysis: Server error details:",
						errorMsg,
					);
					throw new Error(errorMsg);
				}
				throw new Error(error.message || "Reflection analysis failed.");
			}

			if (!data || !data.analysis) {
				console.error("Invalid reflection analysis response format:", data);
				throw new Error("Reflection analysis service returned invalid data.");
			}

			console.log("✅ ai-reflection-analysis: Analysis completed successfully");
			const analysis = data.analysis;

			// Client-side normalization: guarantee required sections are populated
			const ensureArray = (arr: any) => (Array.isArray(arr) ? arr : []);
			const enhanced = { ...analysis } as any;

			enhanced.insights = enhanced.insights || {
				keyFindings: [],
				progressIndicators: [],
				concernAreas: [],
			};
			enhanced.impactTrends = enhanced.impactTrends || {
				overall: "stable",
				recentTrend: "",
				significantChanges: [],
			};

			// Fill recentTrend if empty
			const recentTrendEmpty =
				!enhanced.impactTrends.recentTrend ||
				String(enhanced.impactTrends.recentTrend).trim() === "";
			if (recentTrendEmpty) {
				const total = Array.isArray(checkIns) ? checkIns.length : 0;
				enhanced.impactTrends.recentTrend =
					total > 0
						? `Based on ${total} check-ins in the analysis window`
						: "Insufficient recent check-in data";
			}

			// Ensure insights arrays contain minimum baseline content
			const kf = ensureArray(enhanced.insights.keyFindings);
			const pi = ensureArray(enhanced.insights.progressIndicators);
			const ca = ensureArray(enhanced.insights.concernAreas);

			if (kf.length === 0) {
				kf.push(
					`Reflection analyzed: ${reflectionData.title}`,
					`Linked check-ins considered: ${Array.isArray(checkIns) ? checkIns.length : 0}`,
					`Overall trend: ${enhanced.impactTrends.overall || "stable"}`,
				);
			}
			if (pi.length === 0) {
				pi.push(
					"Evidence of ongoing self-reflection via recent check-ins",
					"Instances of constructive decision-making noted",
				);
			}
			if (ca.length === 0) {
				ca.push(
					"Inconsistent clarity around roles or boundaries",
					"Reliance on external approval may be slowing progress",
				);
			}
			enhanced.insights.keyFindings = kf;
			enhanced.insights.progressIndicators = pi;
			enhanced.insights.concernAreas = ca;

			// Ensure recommendations exist
			const recs = ensureArray(enhanced.recommendations);
			if (recs.length === 0) {
				enhanced.recommendations = [
					{
						action:
							"List 5–8 low‑risk decisions you can make without approval and share the list with your mentor for alignment",
						priority: "high",
						reasoning:
							"Explicit boundaries remove hesitation and speed up routine work",
						timeframe: "weekly",
					},
					{
						action:
							"Run a short priorities review: pick 3 must‑do tasks, note blockers, and confirm who decides each task",
						priority: "medium",
						reasoning:
							"A short weekly cadence builds clarity and reduces last‑minute uncertainty",
						timeframe: "weekly",
					},
					{
						action:
							"After each independent decision, write a 2‑sentence note: what you chose and why; tag it ‘decision log’ to track confidence over time",
						priority: "low",
						reasoning:
							"Capturing decisions in your own words reinforces learning and self‑trust",
						timeframe: "daily",
					},
				];
			}

			console.log("✨ Client-side normalization applied:", {
				keyFindings: enhanced.insights.keyFindings?.length || 0,
				progressIndicators: enhanced.insights.progressIndicators?.length || 0,
				concernAreas: enhanced.insights.concernAreas?.length || 0,
				recommendations: enhanced.recommendations?.length || 0,
				recentTrend: enhanced.impactTrends?.recentTrend || "(empty)",
			});

			return enhanced;
		} catch (error) {
			console.error("Error calling reflection analysis service:", error);
			throw new Error(
				error instanceof Error
					? error.message
					: "Unable to analyze reflection progress",
			);
		}
	}

	private getFallbackCheckInAnalysis() {
		throw new Error(
			"AI analysis service is not configured. Please check your edge functions deployment.",
		);
	}

	async checkAnalysisNeedsUpdate(
		userId: string,
		checkInCount: number,
		latestCheckInDate: string,
	): Promise<boolean> {
		try {
			const today = new Date().toISOString().split("T")[0];
			const { data, error } = await supabase
				.from("daily_checkin_analysis")
				.select("checkin_count, last_checkin_date")
				.eq("user_id", userId)
				.eq("analysis_date", today)
				.single();

			if (error) {
				// Check if table doesn't exist
				if (
					error.message?.includes(
						'relation "public.daily_checkin_analysis" does not exist',
					)
				) {
					console.warn(
						"daily_checkin_analysis table not found, analysis will always be fresh",
					);
				}
				// No existing analysis or table missing, needs update
				return true;
			}

			if (!data) {
				// No existing analysis, needs update
				return true;
			}

			// Check if check-in count or latest date has changed
			const savedDate = new Date(data.last_checkin_date);
			const currentDate = new Date(latestCheckInDate);

			return data.checkin_count !== checkInCount || currentDate > savedDate;
		} catch (error) {
			console.error("Error checking if analysis needs update:", error);
			return true; // Default to needing update if check fails
		}
	}

	async initializeDatabase(): Promise<void> {
		try {
			// Check if prompts exist in database
			const { data, error } = await supabase
				.from("ai_prompts")
				.select("type")
				.limit(1);

			if (error) {
				console.warn("Cannot access ai_prompts table:", error.message || error);
				if (
					error.message?.includes('relation "public.ai_prompts" does not exist')
				) {
					console.warn(
						"ai_prompts table does not exist. Please run the database schema setup.",
					);
				}
				return;
			}

			if (!data || data.length === 0) {
				console.log(
					"No AI prompts found in database, initializing with defaults...",
				);

				// Ensure we have prompts to insert
				if (!this.prompts.reflection_qa) {
					console.error(
						"Default prompts not available, cannot initialize database",
					);
					return;
				}

				// Insert default prompts one by one to identify which one fails
				const promptTypes = [
					{ type: "overall_context", prompt: this.prompts.overall_context },
					{ type: "reflection_qa", prompt: this.prompts.reflection_qa },
					{
						type: "reflection_summary",
						prompt: this.prompts.reflection_summary,
					},
					{
						type: "contributing_factors",
						prompt: this.prompts.contributing_factors,
					},
					{ type: "signals", prompt: this.prompts.signals },
					{
						type: "corrective_actions",
						prompt: this.prompts.corrective_actions,
					},
					{ type: "future_state", prompt: this.prompts.future_state },
				];

				let successCount = 0;
				for (const promptData of promptTypes) {
					try {
						const { error: insertError } = await supabase
							.from("ai_prompts")
							.insert([promptData]);

						if (insertError) {
							console.error(
								`Error inserting ${promptData.type} prompt:`,
								insertError.message || insertError,
							);
						} else {
							successCount++;
							console.log(`Successfully inserted ${promptData.type} prompt`);
						}
					} catch (err) {
						console.error(`Failed to insert ${promptData.type}:`, err);
					}
				}

				if (successCount > 0) {
					console.log(
						`Successfully initialized ${successCount}/${promptTypes.length} AI prompts`,
					);
				}
			} else {
				console.log("AI prompts already exist in database");
			}
		} catch (error) {
			console.error(
				"Error initializing database:",
				error instanceof Error ? error.message : String(error),
			);
		}
	}
}

export const aiService = new AIService();
